import { useMemo } from 'react';
import type { HrSample, HrZone, HrZoneId } from '@/lib/heartRateZones';
import { classifyBpm } from '@/lib/heartRateZones';
import { cn } from '@/lib/utils';

const zoneColors: Record<HrZoneId, string> = {
  z1: 'bg-blue-400',
  z2: 'bg-emerald-400',
  z3: 'bg-amber-400',
  z4: 'bg-orange-400',
  z5: 'bg-red-400',
};

type RoutePoint = { lat: number; lng: number; timestamp?: number };

interface RouteHrEffortChartProps {
  routePoints: RoutePoint[];
  hrSamples: HrSample[];
  zones: HrZone[];
  className?: string;
}

/** Horizontal effort strip: route distance vs heart-rate zone color. */
export function RouteHrEffortChart({
  routePoints,
  hrSamples,
  zones,
  className,
}: RouteHrEffortChartProps) {
  const segments = useMemo(() => {
    if (routePoints.length < 2 || hrSamples.length === 0) return [];

    const sortedHr = [...hrSamples].sort((a, b) => a.timestamp - b.timestamp);
    const startTs = routePoints[0].timestamp ?? sortedHr[0].timestamp;
    const endTs =
      routePoints[routePoints.length - 1].timestamp ?? sortedHr[sortedHr.length - 1].timestamp;
    const duration = Math.max(1, endTs - startTs);

    const bucketCount = Math.min(40, Math.max(12, Math.floor(routePoints.length / 3)));
    const out: { zone: HrZoneId | null; pct: number }[] = [];

    for (let i = 0; i < bucketCount; i++) {
      const t0 = startTs + (duration * i) / bucketCount;
      const t1 = startTs + (duration * (i + 1)) / bucketCount;
      const inBucket = sortedHr.filter((s) => s.timestamp >= t0 && s.timestamp < t1);
      if (inBucket.length === 0) {
        out.push({ zone: null, pct: 100 / bucketCount });
        continue;
      }
      const avg = inBucket.reduce((a, s) => a + s.bpm, 0) / inBucket.length;
      out.push({ zone: classifyBpm(Math.round(avg), zones), pct: 100 / bucketCount });
    }
    return out;
  }, [routePoints, hrSamples, zones]);

  if (segments.length === 0) {
    return (
      <p className={cn('text-[10px] text-muted-foreground text-center py-2', className)}>
        Not enough heart rate + GPS data to show effort along the route.
      </p>
    );
  }

  return (
    <div className={cn('space-y-1.5', className)}>
      <p className="text-[10px] text-muted-foreground text-center">Effort along route (start → finish)</p>
      <div className="flex h-3 rounded-full overflow-hidden border border-border/30">
        {segments.map((seg, i) => (
          <div
            key={i}
            className={cn('h-full', seg.zone ? zoneColors[seg.zone] : 'bg-muted')}
            style={{ width: `${seg.pct}%` }}
          />
        ))}
      </div>
      <div className="flex flex-wrap justify-center gap-x-2 gap-y-0.5">
        {zones.map((z) => (
          <span key={z.id} className="text-[9px] text-muted-foreground flex items-center gap-1">
            <span className={cn('inline-block w-2 h-2 rounded-full', zoneColors[z.id])} />
            {z.label}
          </span>
        ))}
      </div>
    </div>
  );
}
